import { selectionEntities } from "./blocks";
import { hasEffects } from "./effects";
import { edgeExtrema, zeroEdges } from "../geometry/edge-warp";
import { isInteractive } from "./interactive";
import {
  pathPoints,
  pathExtrema,
  vectorPath,
  arrowPoints,
} from "./vector-path";
import { clippingBase } from "./groups";
import { polygonCornerExtrema, roundedPolygonOutline } from "./polygon-corners";
import { cornerRadius, roundedBoxDistance } from "./corners";
import type { Point, SceneNode } from "../scene/model";
import {
  invertAffine,
  transformPoint,
  type AffineMatrix,
} from "../geometry/affine";
import { corners, nodeMatrix } from "./transforms";
import { insidePolygon, polygonOutline, polygonParameters } from "./shapes";
import { hasPaint, shapeStyle, strokeRadius } from "./appearance";
import { distanceToOutline, dashedStrokeContains } from "./stroke";
export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}
export interface Camera {
  x: number;
  y: number;
  zoom: number;
}
export const handles = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
] as const;
export function bounds(node: SceneNode): Box {
  const b = geometryBounds(node),
    radius = strokeRadius(node),
    matrix = nodeMatrix(node);
  const a = matrix[0] / node.geometry.width!,
    b1 = matrix[1] / node.geometry.width!,
    c = matrix[2] / node.geometry.height!,
    d = matrix[3] / node.geometry.height!;
  const dx = radius * Math.hypot(a, c),
    dy = radius * Math.hypot(b1, d);
  return {
    x: b.x - dx,
    y: b.y - dy,
    width: b.width + 2 * dx,
    height: b.height + 2 * dy,
  };
}
function geometryBounds(node: SceneNode): Box {
  const matrix = nodeMatrix(node);
  if (node.geometry.warpCorners)
    return pointBounds(
      edgeExtrema(
        node.geometry.warpCorners.map((p) => transformPoint(p, matrix)),
        (node.geometry.warpEdges ?? zeroEdges()).map((p) => ({
          x: matrix[0] * p.x + matrix[2] * p.y,
          y: matrix[1] * p.x + matrix[3] * p.y,
        })),
      ),
    );
  if (node.geometry.svgPath) return pointBounds(corners(matrix));
  if (node.kind === "path") {
    const vector = vectorPath(node),
      project = (p: Point) => transformPoint(p, matrix);
    return pointBounds([
      ...pathExtrema(
        vector.anchors.map((a) => ({
          ...project(a),
          ...(a.incoming ? { incoming: project(a.incoming) } : {}),
          ...(a.outgoing ? { outgoing: project(a.outgoing) } : {}),
        })),
        vector.closed,
      ),
      ...arrowPoints(node).map(project),
    ]);
  }
  const r = cornerRadius(node);
  if (r > 0 && node.kind === "polygon")
    return pointBounds(polygonCornerExtrema(node, matrix));
  if (r > 0) {
    const w = node.geometry.width!,
      h = node.geometry.height!;
    const a = matrix[0] / w,
      b = matrix[1] / w,
      c = matrix[2] / h,
      d = matrix[3] / h;
    const dx =
      Math.abs(a) * (w / 2 - r) +
      Math.abs(c) * (h / 2 - r) +
      r * Math.hypot(a, c);
    const dy =
      Math.abs(b) * (w / 2 - r) +
      Math.abs(d) * (h / 2 - r) +
      r * Math.hypot(b, d);
    return {
      x: matrix[4] - dx,
      y: matrix[5] - dy,
      width: 2 * dx,
      height: 2 * dy,
    };
  }
  if (node.kind === "ellipse") {
    const width = Math.hypot(matrix[0], matrix[2]),
      height = Math.hypot(matrix[1], matrix[3]);
    return {
      x: matrix[4] - width / 2,
      y: matrix[5] - height / 2,
      width,
      height,
    };
  }
  return pointBounds(
    node.kind === "polygon"
      ? polygonOutline(polygonParameters(node)).points.map((p) =>
          transformPoint(p, matrix),
        )
      : corners(matrix),
  );
}
export function pointBounds(points: readonly Point[]): Box {
  const x = Math.min(...points.map((p) => p.x)),
    y = Math.min(...points.map((p) => p.y));
  return {
    x,
    y,
    width: Math.max(...points.map((p) => p.x)) - x,
    height: Math.max(...points.map((p) => p.y)) - y,
  };
}
export function selectionFrame(
  nodes: readonly SceneNode[],
): AffineMatrix | null {
  nodes = selectionEntities(nodes);
  if (!nodes.length) return null;
  if (nodes.length === 1) return nodeMatrix(nodes[0]!);
  const b = pointBounds(
    nodes.flatMap((n) => {
      const box = geometryBounds(n);
      return [
        { x: box.x, y: box.y },
        { x: box.x + box.width, y: box.y + box.height },
      ];
    }),
  );
  return [b.width, 0, 0, b.height, b.x + b.width / 2, b.y + b.height / 2];
}
export function selectable(node: SceneNode) {
  return node.appearance.visible !== false && !node.layer.locked;
}
export function insideNode(node: SceneNode, point: Point, tolerance = 0) {
  if (node.geometry.warpCorners || node.geometry.svgPath) {
    const b = bounds(node);
    return (
      point.x >= b.x &&
      point.y >= b.y &&
      point.x <= b.x + b.width &&
      point.y <= b.y + b.height
    );
  }

  if (!hasPaint(node)) return false;
  const inverse = invertAffine(nodeMatrix(node));
  if (!inverse) return false;
  const p = transformPoint(point, inverse);
  if (node.kind === "path") {
    const points = pathPoints(node).map((q) =>
      transformPoint(q, nodeMatrix(node)),
    );
    const arrow = arrowPoints(node);
    if (arrow.length && insidePolygon(arrow, p)) return true;
    if (
      vectorPath(node).closed &&
      shapeStyle(node).fillEnabled &&
      insidePolygon(pathPoints(node), p)
    )
      return true;
    const radius = Math.max(tolerance, strokeRadius(node));
    return points.slice(1).some((q, i) => {
      const a = points[i]!,
        dx = q.x - a.x,
        dy = q.y - a.y,
        t = Math.max(
          0,
          Math.min(
            1,
            ((point.x - a.x) * dx + (point.y - a.y) * dy) /
              (dx * dx + dy * dy || 1),
          ),
        );
      return (
        Math.hypot(point.x - a.x - t * dx, point.y - a.y - t * dy) <= radius
      );
    });
  }
  const filled =
    node.kind === "polygon" && cornerRadius(node) > 0
      ? insidePolygon(roundedPolygonOutline(node), {
          x: p.x * node.geometry.width!,
          y: p.y * node.geometry.height!,
        })
      : cornerRadius(node) > 0
        ? roundedBoxDistance(node, {
            x: p.x * node.geometry.width!,
            y: p.y * node.geometry.height!,
          }) <= 1e-10
        : node.kind === "ellipse"
          ? p.x * p.x + p.y * p.y <= 0.25 + 1e-12
          : node.kind === "polygon"
            ? insidePolygon(polygonOutline(polygonParameters(node)).points, p)
            : contains({ x: -0.5, y: -0.5, width: 1, height: 1 }, p);
  if (
    filled &&
    (node.kind === "image" ||
      isInteractive(node) ||
      shapeStyle(node).fillEnabled)
  )
    return true;
  const radius = strokeRadius(node);
  if (radius > 0 && shapeStyle(node).lineStyle !== "solid")
    return dashedStrokeContains(node, {
      x: p.x * node.geometry.width!,
      y: p.y * node.geometry.height!,
    });
  return (
    radius > 0 &&
    distanceToOutline(node, {
      x: p.x * node.geometry.width!,
      y: p.y * node.geometry.height!,
    }) <=
      radius + 1e-8
  );
}
export function marqueeNodes(nodes: readonly SceneNode[], box: Box) {
  return nodes.filter((n) => {
    const b = bounds(n);
    return (
      selectable(n) &&
      hasPaint(n) &&
      contains(box, { x: b.x, y: b.y }) &&
      contains(box, { x: b.x + b.width, y: b.y + b.height })
    );
  });
}
export function frameHandles(frame: AffineMatrix) {
  return handles.map(([x, y]) => transformPoint({ x: x / 2, y: y / 2 }, frame));
}
export function rotationHandles(frame: AffineMatrix, zoom: number) {
  return corners(frame).map((p) => {
    const dx = p.x - frame[4],
      dy = p.y - frame[5],
      length = Math.hypot(dx, dy);
    return {
      x: p.x + ((dx / length) * 23) / zoom,
      y: p.y + ((dy / length) * 23) / zoom,
    };
  });
}
export function hitFrameHandle(
  frame: AffineMatrix,
  p: Point,
  zoom: number,
  touch = false,
) {
  const tolerance = (touch ? 16 : 9) / zoom;
  const matches = frameHandles(frame)
    .map((q, index) => ({
      q,
      index,
      distance: Math.hypot(p.x - q.x, p.y - q.y),
    }))
    .filter(
      ({ q }) =>
        Math.abs(p.x - q.x) <= tolerance && Math.abs(p.y - q.y) <= tolerance,
    )
    .sort((a, b) => a.distance - b.distance);
  return matches[0]?.index ?? -1;
}
export function hitRotation(
  frame: AffineMatrix,
  p: Point,
  zoom: number,
  touch = false,
) {
  return rotationHandles(frame, zoom).some(
    (q) => Math.hypot(p.x - q.x, p.y - q.y) <= (touch ? 16 : 11) / zoom,
  );
}
export function documentPoint(p: Point, camera: Camera): Point {
  return {
    x: (p.x - camera.x) / camera.zoom,
    y: (p.y - camera.y) / camera.zoom,
  };
}
export function contains(box: Box, p: Point) {
  return (
    p.x >= box.x &&
    p.y >= box.y &&
    p.x <= box.x + box.width &&
    p.y <= box.y + box.height
  );
}
let pixelHit:
  | ((node: SceneNode, p: Point, nodes: readonly SceneNode[]) => boolean)
  | undefined;
export function setPixelHitTest(test: typeof pixelHit) {
  pixelHit = test;
}
export function hitNode(nodes: readonly SceneNode[], p: Point, zoom = 1) {
  return nodes
    .slice()
    .reverse()
    .find(
      (n) =>
        selectable(n) &&
        insideNode(n, p, n.kind === "path" ? 7 / zoom : 0) &&
        (!(n.layer.clipToBelow || n.geometry.svgPath || hasEffects(n)) ||
          (pixelHit
            ? pixelHit(n, p, nodes)
            : !!clippingBase(nodes, n) &&
              insideNode(clippingBase(nodes, n)!, p))),
    );
}
export function hitHandle(
  node: SceneNode,
  p: Point,
  zoom: number,
  touch = false,
) {
  return hitFrameHandle(nodeMatrix(node), p, zoom, touch);
}
export function drawBox(
  start: Point,
  end: Point,
  free: boolean,
  centered: boolean,
): Box {
  let dx = end.x - start.x,
    dy = end.y - start.y;
  if (!free) {
    const size = Math.max(Math.abs(dx), Math.abs(dy));
    dx = (dx < 0 ? -1 : 1) * size;
    dy = (dy < 0 ? -1 : 1) * size;
  }
  return {
    x: centered ? start.x - Math.abs(dx) : Math.min(start.x, start.x + dx),
    y: centered ? start.y - Math.abs(dy) : Math.min(start.y, start.y + dy),
    width: Math.abs(dx) * (centered ? 2 : 1),
    height: Math.abs(dy) * (centered ? 2 : 1),
  };
}
export function withBox(node: SceneNode, box: Box): SceneNode {
  return {
    ...node,
    transform: {
      ...node.transform,
      x: box.x + box.width / 2,
      y: box.y + box.height / 2,
    },
    geometry: { ...node.geometry, width: box.width, height: box.height },
  };
}
